import { faBed, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RoutePlanTbLevelEnum, RoutePlanWarningEnum } from 'api/model';
import TooltipInfo from 'components/TooltipInfo';
import React from 'react';
import styled from 'styled-components';
import { formatDurationMs } from './selection-helpers';
import {
  FALLBACK_REVENUE_PER_HOUR_SEK,
  formatBufferPercent,
  formatSekApprox,
  STANDARD_HOURLY_COST_SEK,
  tbBreakdown,
  tbClassLabel,
  tbClassMeaning,
  tbUncertaintyNotes,
} from './tb-language';
import { getWarningMeta, WarningSeverity } from './warning-helpers';

const severityColors: Record<WarningSeverity, { bg: string; fg: string }> = {
  blocking: { bg: 'rgb(255,0,0)', fg: '#fff' },
  decision: { bg: '#ffbf00', fg: '#000' },
  info: { bg: 'rgb(235,235,235)', fg: '#000' },
};

/**
 * Exporterad så att den manuella listan kan visa sina orsaker i samma toner som paketkortens
 * varningar. Två uppsättningar färger för samma allvarlighetsgrad hade läst som två olika språk.
 */
export const SeverityPill = styled.span<{ severity: WarningSeverity }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.85em;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ severity }) => severityColors[severity].bg};
  color: ${({ severity }) => severityColors[severity].fg};
`;

const Pill = SeverityPill;

export const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const tbColors: Record<RoutePlanTbLevelEnum, { bg: string; fg: string }> = {
  [RoutePlanTbLevelEnum.Green]: { bg: 'rgba(0,187,0,0.15)', fg: 'rgb(0,110,0)' },
  [RoutePlanTbLevelEnum.Yellow]: { bg: '#ffbf00', fg: '#000' },
  [RoutePlanTbLevelEnum.Red]: { bg: 'rgb(255,0,0)', fg: '#fff' },
  [RoutePlanTbLevelEnum.Unknown]: { bg: 'rgb(235,235,235)', fg: 'rgb(120,120,120)' },
};

const TbPill = styled.span<{ level: RoutePlanTbLevelEnum }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.85em;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ level }) => tbColors[level].bg};
  color: ${({ level }) => tbColors[level].fg};
`;

/**
 * Beloppet intill ordet — medvetet OFÄRGAT. Två färgade chip som säger olika saker gör att grönt
 * betyder två olika saker och ingen vet vilket som styr beslutet. Ordet bär domen, talet bär
 * precisionen.
 */
const TbAmount = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 3px;
  border: 1px solid rgb(220, 220, 220);
  background: rgb(250, 250, 250);
  color: rgb(70, 70, 70);
  font-size: 0.85em;
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const TbGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const TipPanel = styled.div`
  width: 340px;
  max-width: 78vw;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 2px;
  font-size: 0.9rem;
  font-weight: 400;
  color: rgb(40, 40, 40);
  white-space: normal;
  text-align: left;
`;

const TipMeaning = styled.div`
  font-weight: 600;
`;

const TipRows = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 3px 10px;
  align-items: baseline;
  font-variant-numeric: tabular-nums;
  border-top: 1px solid rgb(230, 230, 230);
  border-bottom: 1px solid rgb(230, 230, 230);
  padding: 7px 0;
`;

const TipLabel = styled.span`
  color: rgb(110, 110, 110);
`;

const TipSource = styled.span`
  color: rgb(70, 70, 70);
  font-size: 0.92em;
`;

const TipValue = styled.span`
  text-align: right;
  font-weight: 600;
`;

const TipSum = styled.span`
  font-weight: 700;
`;

const TipValueStrong = styled(TipValue)`
  font-weight: 700;
`;

const TipNotes = styled.ul`
  margin: 0;
  padding-left: 16px;
  color: rgb(110, 110, 110);
  font-size: 0.92em;
  line-height: 1.45;

  li + li {
    margin-top: 3px;
  }
`;

interface TbBadgeProps {
  level: RoutePlanTbLevelEnum;
  bufferPercent?: number;
  compact?: boolean;
  /** True = intäkten är schablon (prislista saknades), inte ett prislistepris. */
  estimated?: boolean;
  /** True = minst ett uppdrags arbetstid är uppskattad, inte bokad. */
  durationIsEstimated?: boolean;
  /** Prislisteintäkten i kr. Saknas den finns ingen uträkning att visa i hovern. */
  revenueSek?: number | null;
  /** Schablonkostnaden i kr — arbetstid + överföring, rast borträknad. */
  costSek?: number;
  /** Tidsunderlaget för kostnaden (ms), visas som källa i kostnadsraden. */
  costBasisMs?: number;
  /** Valda lucka-åtgärders intäktsdel (paketnivå). */
  remedyRevenueSek?: number;
  /** Valda lucka-åtgärders kostnadsdel (paketnivå). */
  remedyCostSek?: number;
  /** Hotellnatt krävs men är oprissatt — kapar grönt och nämns i hovern. */
  hotelUnpriced?: boolean;
  /** En vald åtgärd saknar prisrad, så TB inte kan beräknas. */
  unpricedRemedy?: boolean;
  /** Vems TB det är: paketets (åtgärder ingår) eller ett uppdrags eget. */
  scope?: 'package' | 'leg';
  /** True = visa det exakta TB-beloppet i en neutral badge intill ordet. */
  showAbsolute?: boolean;
  /** Hoverns kant. 'right' i paketheadern, där chippet sitter längst ut till höger. */
  align?: 'left' | 'right';
}

/**
 * TB som ord, med uträkningen i hovern.
 *
 * Klassen är absolut (grön >10 % buffert, gul 0–10, röd negativ, grå = pris saknas) och kommer från
 * backend (<c>RoutePlanningLogic.ComputeTb</c>). Undantaget är paketets TB när trafikledaren valt en
 * lucka-åtgärd: den väljs efter att förslaget byggts, så <c>tb-remedies.tbWithRemedies</c> justerar
 * talen och räknar om klassen med speglade trösklar. Komponenten själv klassificerar aldrig — den
 * visar den nivå den får. Talen avrundas till hundralapp och TB räknas ur de avrundade talen, så
 * subtraktionen går ihop för den som kontrollerar den.
 *
 * Se .claude/features/route-planning.md ("TB-språket i kortet").
 */
export const TbBadge: React.FC<TbBadgeProps> = ({
  level,
  bufferPercent,
  compact,
  estimated,
  durationIsEstimated,
  revenueSek,
  costSek,
  costBasisMs,
  remedyRevenueSek,
  remedyCostSek,
  hotelUnpriced,
  unpricedRemedy,
  scope = 'package',
  showAbsolute,
  align,
}) => {
  if (level === RoutePlanTbLevelEnum.Unknown && compact) {
    return null;
  }

  const hasCalculation =
    revenueSek != null && revenueSek > 0 && costSek != null && !unpricedRemedy;
  const breakdown = hasCalculation
    ? tbBreakdown(revenueSek, costSek, remedyRevenueSek ?? 0, remedyCostSek ?? 0)
    : undefined;

  const info = (
    <TipPanel>
      <TipMeaning>{tbClassMeaning(level)}</TipMeaning>

      {breakdown && (
        <TipRows>
          <TipLabel>Intäkt</TipLabel>
          <TipSource>
            {estimated
              ? `schablon ${FALLBACK_REVENUE_PER_HOUR_SEK} kr/h`
              : 'prislista'}
          </TipSource>
          <TipValue>{formatSekApprox(breakdown.revenueSek)}</TipValue>

          {breakdown.remedyRevenueSek > 0 && (
            <>
              <TipLabel>Åtgärder</TipLabel>
              <TipSource>valda lucka-åtgärder, intäkt</TipSource>
              <TipValue>{formatSekApprox(breakdown.remedyRevenueSek)}</TipValue>
            </>
          )}

          <TipLabel>Kostnad</TipLabel>
          <TipSource>
            schablonlön {STANDARD_HOURLY_COST_SEK} kr/h
            {costBasisMs ? ` · ${formatDurationMs(costBasisMs)}` : ''}
          </TipSource>
          <TipValue>{formatSekApprox(-breakdown.costSek)}</TipValue>

          {breakdown.remedyCostSek > 0 && (
            <>
              <TipLabel>Åtgärder</TipLabel>
              <TipSource>valda lucka-åtgärder, kostnad</TipSource>
              <TipValue>{formatSekApprox(-breakdown.remedyCostSek)}</TipValue>
            </>
          )}

          <TipSum>TB</TipSum>
          <TipSource>
            {bufferPercent != null
              ? `buffert ${formatBufferPercent(bufferPercent)}`
              : ''}
          </TipSource>
          <TipValueStrong>{formatSekApprox(breakdown.tbSek)}</TipValueStrong>
        </TipRows>
      )}

      {/* Utan uträkning finns inget tal att kvalificera — meningen ovan säger redan att
          prislistan saknar rad, och schablonlönen är då ovidkommande. */}
      {(() => {
        const notes = tbUncertaintyNotes({
          revenueIsEstimated: estimated === true,
          durationIsEstimated: durationIsEstimated === true,
          scope,
          hotelUnpriced,
          unpricedRemedy,
          // Utan uträkning finns inget tal att kvalificera — men en oprissatt åtgärd eller ett
          // hotell är själva förklaringen till klassen och ska sägas ändå.
        }).filter((note) => breakdown != null || note.includes('prisrad') || note.includes('Hotellnatt'));

        return notes.length > 0 ? (
          <TipNotes>
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </TipNotes>
        ) : null;
      })()}
    </TipPanel>
  );

  return (
    <TbGroup>
      <TooltipInfo
        focusable
        hoverable
        info={info}
        node={<TbPill level={level}>{tbClassLabel(level)}</TbPill>}
        nodeAlign={align}
      />
      {showAbsolute && breakdown && (
        <TbAmount
          title="Exakt estimat — visas för superadmin och när RoutePlanAbsoluteTbEnabled är på"
        >
          TB {formatSekApprox(breakdown.tbSek)}
          {bufferPercent != null
            ? ` · ${formatBufferPercent(bufferPercent)}`
            : ''}
        </TbAmount>
      )}
    </TbGroup>
  );
};

interface Props {
  warning: RoutePlanWarningEnum;
}

const WarningPill: React.FC<Props> = ({ warning }) => {
  const meta = getWarningMeta(warning);
  return (
    <Pill severity={meta.severity} title={meta.label}>
      {meta.icon === 'bed' && <FontAwesomeIcon icon={faBed} />}
      {meta.severity === 'blocking' && meta.icon !== 'bed' && (
        <FontAwesomeIcon icon={faTriangleExclamation} />
      )}
      {meta.label}
    </Pill>
  );
};

export default WarningPill;
